import { AxiosError, isAxiosError } from 'axios';
import { MutableRefObject } from 'react';

import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { getProfileType } from '@app/dashboard/utils/localStorageUtils';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { PROFILE } from '@app/dashboard/constants/profile';

import {
  ACTIONS,
  API_KEYS_TYPES,
} from '@app/dashboard/constants/settings/api-keys';
import type {
  ICreatePayload,
  TApiKeysActions,
  TApiKeysCardType,
} from '@app/dashboard/types/innerLayout/settings/api_keys';
import {
  createRevokeApiKey,
  createRevokePublicSecretKey,
} from '@app/dashboard/services/inner_layout/api_keys';

const checkPermission = (permission: string) => {
  const profileType = getProfileType();

  return (
    profileType === PROFILE.TYPE.MERCHANT ||
    (profileType === PROFILE.TYPE.MERCHANT_STAFF && isAccessible(permission))
  );
};

export const checkIfStaff = () =>
  getProfileType() === PROFILE.TYPE.MERCHANT_STAFF;

export const getKeysPermissions = () => ({
  canViewApiKey:
    checkPermission(PERMISSIONS.CAN_VIEW_API_KEY) ||
    checkPermission(PERMISSIONS.CAN_UPDATE_API_KEY),
  canUpdateApiKey: checkPermission(PERMISSIONS.CAN_UPDATE_API_KEY),
  canViewPublicSecretKey:
    checkPermission(PERMISSIONS.CAN_VIEW_PUBLIC_SECRET_KEY) ||
    checkPermission(PERMISSIONS.CAN_UPDATE_PUBLIC_SECRET_KEY),
  canUpdatePublicSecretKey: checkPermission(
    PERMISSIONS.CAN_UPDATE_PUBLIC_SECRET_KEY
  ),
});

export const shouldHideCard = (type: TApiKeysCardType) => {
  const permissions = getKeysPermissions();

  const apiKeyCard = type === API_KEYS_TYPES.API_KEYS;
  const publicSecretCard =
    type === API_KEYS_TYPES.PUBLIC_KEY || type === API_KEYS_TYPES.SECRET_KEY;
  const hmacCard = type === API_KEYS_TYPES.HMAC;

  if (
    (apiKeyCard && !permissions.canViewApiKey) ||
    (publicSecretCard && !permissions.canViewPublicSecretKey) ||
    (hmacCard && checkIfStaff())
  )
    return true;
};

export const checkIfServerError = ({
  type,
  isApiKeyError,
  isPublicSecretKeyError,
  isHmacError,
}: {
  type: TApiKeysCardType;
  isApiKeyError: boolean;
  isPublicSecretKeyError: boolean;
  isHmacError: boolean;
}) => {
  const isSecretPublicKey = (
    [API_KEYS_TYPES.PUBLIC_KEY, API_KEYS_TYPES.SECRET_KEY] as TApiKeysCardType[]
  ).includes(type);

  const disableApiCardBtns = type === API_KEYS_TYPES.API_KEYS && isApiKeyError;

  const disablePublicSecretKeyBtns =
    isSecretPublicKey && isPublicSecretKeyError;

  const disableHmacBtns = type === API_KEYS_TYPES.HMAC && isHmacError;

  const isServerError =
    disableApiCardBtns || disablePublicSecretKeyBtns || disableHmacBtns;

  return isServerError;
};

export const shouldHideBtns = ({
  type,
  action,
  permissions,
}: {
  type: TApiKeysCardType;
  action: TApiKeysActions;
  permissions: ReturnType<typeof getKeysPermissions>;
}): boolean => {
  const isApiKey = type === API_KEYS_TYPES.API_KEYS;
  const isSecretPublicKey = (
    [API_KEYS_TYPES.PUBLIC_KEY, API_KEYS_TYPES.SECRET_KEY] as TApiKeysCardType[]
  ).includes(type);

  const isViewOrCopyAction = [ACTIONS.VIEW, ACTIONS.COPY].includes(action);
  const isRecreateAction = action === ACTIONS.RECREATE;

  const hidedViewOrCopyApiKey =
    isViewOrCopyAction && isApiKey && !permissions.canViewApiKey;

  const hideRecreateApiKey =
    isRecreateAction && isApiKey && !permissions.canUpdateApiKey;

  const disabledViewOrCopyPublicOrSecretKey =
    isViewOrCopyAction &&
    isSecretPublicKey &&
    !permissions.canViewPublicSecretKey;

  const hideRecreatePublicOrSecretKey =
    isRecreateAction &&
    isSecretPublicKey &&
    !permissions.canUpdatePublicSecretKey;

  return (
    hidedViewOrCopyApiKey ||
    hideRecreateApiKey ||
    hideRecreatePublicOrSecretKey ||
    disabledViewOrCopyPublicOrSecretKey
  );
};

export const getErrorMessage = (
  error: AxiosError<{ message?: string }> | Error | null
) => {
  if (isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message;
  }
  return undefined;
};

export const handleCopyText = async ({
  textToCopy,
  copyTimeoutRef,
  setIsCopied,
}: {
  textToCopy: string;
  copyTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  setIsCopied: any;
}) => {
  if (textToCopy) {
    try {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  }
};

export const getApiMapper = (isLive: boolean) => {
  const keyType = isLive ? 'live' : 'test';

  return {
    [API_KEYS_TYPES.API_KEYS]: (operation: ICreatePayload['operation']) =>
      createRevokeApiKey(),

    [API_KEYS_TYPES.SECRET_KEY]: (operation: ICreatePayload['operation']) =>
      createRevokePublicSecretKey({
        type: 'secret',
        payload: {
          key_type: keyType,
          operation,
        },
      }),

    [API_KEYS_TYPES.PUBLIC_KEY]: (operation: ICreatePayload['operation']) =>
      createRevokePublicSecretKey({
        type: 'public',
        payload: {
          key_type: keyType,
          operation,
        },
      }),
  };
};
